import React, { useState, useEffect } from 'react';
import { 
  DollarSign, CreditCard, TrendingUp, Calendar, Building,
  Wallet, PiggyBank, Percent, Info, History, Award, MapPin, 
  FileText, Shield, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Separator } from '../../ui/separator';
import { Progress } from '../../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { db } from '../../../lib/db';
import { formatCurrency } from '../../../lib/payrollCalculations';
import { formatDate, getMonthName, getCurrentMonth, getCurrentYear } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { STATE_NAMES } from '../../../lib/usPayrollData';
import { 
  calculateFederalIncomeTax, 
  calculateSocialSecurityTax, 
  calculateMedicareTax,
  calculateStateIncomeTax,
  FICA_RATES
} from '../../../lib/usPayrollCalculations';

// Filing status labels for display
const FILING_STATUS_LABELS = {
  single: 'Single',
  married_jointly: 'Married Filing Jointly',
  married_separately: 'Married Filing Separately',
  head_of_household: 'Head of Household'
};

export default function MySalaryInfo() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [salaryStructure, setSalaryStructure] = useState(null);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [w4Data, setW4Data] = useState(null);
  const [compensation, setCompensation] = useState(null);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      const [salaryData, bonusData, payslipData, w4, compData] = await Promise.all([
        db.salaryStructures.where('userId').equals(user.id).first(),
        db.bonuses.where('userId').equals(user.id).toArray(),
        db.payslips.where('userId').equals(user.id).toArray(),
        db.employeeW4.where('userId').equals(user.id).first(),
        db.employeeCompensation.where('userId').equals(user.id).first()
      ]);
      
      setSalaryStructure(salaryData);
      setSalaryHistory(salaryData?.history || []);
      setBonuses(bonusData.filter(b => b.status === 'approved' || b.status === 'paid').sort((a, b) => (b.approvedAt || b.createdAt) - (a.approvedAt || a.createdAt)));
      setPayslips(payslipData);
      setW4Data(w4);
      setCompensation(compData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!compensation && !salaryStructure) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <Wallet className="w-16 h-16 text-gray-300 mb-4" />
        <p className="text-lg font-medium">Compensation information not available</p>
        <p className="text-sm">Please contact HR for your pay details</p>
      </div>
    );
  }

  // Use US Compensation data if available, otherwise fallback to legacy salaryStructure
  const isUSPayroll = !!compensation;
  const payRate = compensation?.payRate || 0;
  const payType = compensation?.payType || 'salary';
  const payFrequency = compensation?.payFrequency || 'biweekly';
  
  // Calculate gross salary based on US payroll data
  let grossSalary, annualSalary;
  if (isUSPayroll) {
    // Calculate periods per year based on pay frequency
    const periodsPerYear = payFrequency === 'monthly' ? 12 : 
                           payFrequency === 'semimonthly' ? 24 : 
                           payFrequency === 'biweekly' ? 26 : 
                           payFrequency === 'weekly' ? 52 : 26;
    
    // Calculate hours per pay period based on frequency
    const hoursPerPeriod = payFrequency === 'monthly' ? 173.33 :
                           payFrequency === 'semimonthly' ? 86.67 :
                           payFrequency === 'biweekly' ? 80 :
                           payFrequency === 'weekly' ? 40 : 80;
    
    if (payType === 'salary') {
      annualSalary = payRate;
      grossSalary = payRate / periodsPerYear;
    } else {
      // Hourly: calculate based on hours per period
      grossSalary = payRate * hoursPerPeriod;
      annualSalary = payRate * 2080; // Standard 40 hrs * 52 weeks
    }
  } else {
    // Legacy salary structure
    const components = {
      basic: salaryStructure?.basicSalary || 0,
      hra: salaryStructure?.hra || 0,
      transportAllowance: salaryStructure?.transportAllowance || 0,
      medicalAllowance: salaryStructure?.medicalAllowance || 0,
      specialAllowance: salaryStructure?.specialAllowance || 0,
      ...(salaryStructure?.components || {})
    };
    grossSalary = salaryStructure?.grossSalary || Object.values(components).reduce((sum, val) => sum + (Number(val) || 0), 0);
    annualSalary = grossSalary * 12;
  }
  
  const totalBonuses = bonuses.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Salary Information</h1>
        <p className="text-gray-500 mt-1">View your compensation details and breakdown</p>
      </div>

      {/* Salary Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-r from-teal-500 to-emerald-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100">
                  {isUSPayroll 
                    ? `${payFrequency.charAt(0).toUpperCase() + payFrequency.slice(1)} Gross`
                    : 'Monthly Gross'}
                </p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(grossSalary)}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Annual CTC</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(annualSalary)}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Bonuses</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(totalBonuses)}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Award className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="breakdown" className="space-y-6">
        <TabsList className="bg-gray-100/80">
          <TabsTrigger value="breakdown">Salary Breakdown</TabsTrigger>
          <TabsTrigger value="bonuses">Bonuses & Incentives</TabsTrigger>
          <TabsTrigger value="history">Revision History</TabsTrigger>
        </TabsList>

        {/* Salary Breakdown */}
        <TabsContent value="breakdown">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-teal-600" />
                  Compensation Details
                </CardTitle>
                <CardDescription>Your pay structure</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {isUSPayroll ? (
                  <>
                    <div className="p-4 bg-emerald-50 rounded-lg space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Pay Type</span>
                        <Badge variant="outline" className="capitalize">{payType}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Pay Rate</span>
                        <span className="font-bold text-emerald-600">
                          {payType === 'hourly' ? `${formatCurrency(payRate)}/hr` : formatCurrency(payRate)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Pay Frequency</span>
                        <Badge variant="outline" className="capitalize">{payFrequency}</Badge>
                      </div>
                      <Separator />
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Work State</span>
                        <Badge>{compensation?.workState || user?.profile?.workState}</Badge>
                      </div>
                      {compensation?.residentState && compensation.residentState !== compensation?.workState && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700">Resident State</span>
                          <Badge variant="outline">{compensation.residentState}</Badge>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">FLSA Status</span>
                        <Badge variant="outline" className="capitalize">{compensation?.flsaStatus || 'Exempt'}</Badge>
                      </div>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center pt-2">
                      <span className="font-semibold text-gray-900">Per Pay Period</span>
                      <span className="text-xl font-bold text-teal-600">{formatCurrency(grossSalary)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Annual Compensation</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(annualSalary)}</span>
                    </div>
                  </>
                ) : (
                  // Legacy format display - kept for backward compatibility
                  <>
                    {salaryStructure && Object.entries({
                      basic: salaryStructure.basicSalary || 0,
                      hra: salaryStructure.hra || 0,
                      transportAllowance: salaryStructure.transportAllowance || 0,
                      medicalAllowance: salaryStructure.medicalAllowance || 0,
                      specialAllowance: salaryStructure.specialAllowance || 0
                    }).filter(([_, value]) => value > 0).map(([key, value]) => {
                      const percentage = (value / grossSalary) * 100;
                      return (
                        <div key={key} className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700 capitalize">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                            <span className="font-semibold">{formatCurrency(value)}</span>
                          </div>
                          <Progress value={percentage} className="h-2" />
                          <p className="text-xs text-gray-500 text-right">{percentage.toFixed(1)}% of gross</p>
                        </div>
                      );
                    })}
                    <Separator />
                    <div className="flex justify-between items-center pt-2">
                      <span className="font-semibold text-gray-900">Gross Salary</span>
                      <span className="text-xl font-bold text-teal-600">{formatCurrency(grossSalary)}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-600" />
                  Salary Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Pay Grade</p>
                      <p className="font-semibold text-gray-900">{salaryStructure?.payGrade || compensation?.payGrade || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Pay Frequency</p>
                      <p className="font-semibold text-gray-900 capitalize">{isUSPayroll ? payFrequency : 'Monthly'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Effective From</p>
                      <p className="font-semibold text-gray-900">{formatDate(compensation?.effectiveDate || salaryStructure?.effectiveFrom)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Last Updated</p>
                      <p className="font-semibold text-gray-900">{formatDate(compensation?.updatedAt || salaryStructure?.updatedAt)}</p>
                    </div>
                  </div>
                </div>

                {/* Show actual payslip data if available, otherwise show estimates */}
                {payslips.length > 0 ? (
                  // Sort payslips by date and get the latest
                  (() => {
                    const sortedPayslips = [...payslips].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
                    const latestPayslip = sortedPayslips[0];
                    const taxes = latestPayslip.taxes || {};
                    
                    return (
                      <div className="p-4 bg-emerald-50 rounded-lg">
                        <h4 className="font-medium text-emerald-900 mb-3 flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Latest Payslip Tax Withholdings
                          <Badge variant="outline" className="ml-2 text-xs">
                            {latestPayslip.periodName || `${latestPayslip.month}/${latestPayslip.year}`}
                          </Badge>
                        </h4>
                        <div className="space-y-3 text-sm">
                          {/* Gross Pay */}
                          <div className="flex justify-between items-center">
                            <span className="text-emerald-700 font-medium">Gross Pay</span>
                            <span className="font-bold text-emerald-900">{formatCurrency(latestPayslip.grossPay || latestPayslip.grossSalary || 0)}</span>
                          </div>
                          
                          <Separator className="my-2" />
                          
                          {/* Federal Income Tax */}
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <span className="text-emerald-700">Federal Income Tax</span>
                              {latestPayslip.w4Info?.filingStatus && (
                                <Badge variant="outline" className="text-xs">
                                  {FILING_STATUS_LABELS[latestPayslip.w4Info.filingStatus] || latestPayslip.w4Info.filingStatus}
                                </Badge>
                              )}
                            </div>
                            <span className="font-medium text-emerald-900">
                              {formatCurrency(taxes.federalIncome || taxes.federal || 0)}
                            </span>
                          </div>
                          
                          {/* Social Security */}
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <span className="text-emerald-700">Social Security</span>
                              <span className="text-xs text-emerald-500">(6.20%)</span>
                            </div>
                            <span className="font-medium text-emerald-900">
                              {formatCurrency(taxes.socialSecurity || 0)}
                            </span>
                          </div>
                          
                          {/* Medicare */}
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <span className="text-emerald-700">Medicare</span>
                              <span className="text-xs text-emerald-500">(1.45%)</span>
                            </div>
                            <span className="font-medium text-emerald-900">
                              {formatCurrency(taxes.medicare || 0)}
                            </span>
                          </div>
                          
                          {/* State Tax */}
                          {(taxes.stateIncome > 0 || taxes.stateName) && (
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2">
                                <span className="text-emerald-700">State Tax</span>
                                <Badge variant="outline" className="text-xs">
                                  {STATE_NAMES[taxes.stateName || latestPayslip.workState] || taxes.stateName || latestPayslip.workState}
                                </Badge>
                              </div>
                              <span className="font-medium text-emerald-900">
                                {formatCurrency(taxes.stateIncome || 0)}
                              </span>
                            </div>
                          )}
                          
                          <Separator className="my-2" />
                          
                          {/* Total Taxes */}
                          <div className="flex justify-between items-center font-semibold">
                            <span className="text-emerald-900">Total Tax Withholding</span>
                            <span className="text-emerald-900">
                              {formatCurrency(latestPayslip.totalTaxes || 
                                ((taxes.federalIncome || 0) + (taxes.socialSecurity || 0) + (taxes.medicare || 0) + (taxes.stateIncome || 0))
                              )}
                            </span>
                          </div>
                          
                          <Separator className="my-2" />
                          
                          {/* Net Pay */}
                          <div className="flex justify-between items-center">
                            <span className="text-emerald-700 font-medium">Net Pay (Take Home)</span>
                            <span className="font-bold text-lg text-emerald-900">{formatCurrency(latestPayslip.netPay || latestPayslip.netSalary || 0)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      US Tax Withholdings (Estimated Per Pay Period)
                    </h4>
                    <div className="space-y-3 text-sm">
                      {/* Federal Income Tax */}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="text-blue-700">Federal Income Tax</span>
                          {w4Data && (
                            <Badge variant="outline" className="text-xs">
                              {FILING_STATUS_LABELS[w4Data.filingStatus] || w4Data.filingStatus}
                            </Badge>
                          )}
                        </div>
                        <span className="font-medium text-blue-900">
                          {formatCurrency(calculateFederalIncomeTax(
                            annualSalary,
                            w4Data?.filingStatus || 'single',
                            w4Data?.additionalWithholding || 0,
                            isUSPayroll ? (payFrequency === 'monthly' ? 12 : payFrequency === 'semimonthly' ? 24 : payFrequency === 'biweekly' ? 26 : 52) : 12
                          ))}
                        </span>
                      </div>
                      
                      {/* Social Security */}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="text-blue-700">Social Security</span>
                          <span className="text-xs text-blue-500">({(FICA_RATES.socialSecurity.rate * 100).toFixed(2)}%)</span>
                        </div>
                        <span className="font-medium text-blue-900">
                          {formatCurrency(calculateSocialSecurityTax(grossSalary))}
                        </span>
                      </div>
                      
                      {/* Medicare */}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="text-blue-700">Medicare</span>
                          <span className="text-xs text-blue-500">({(FICA_RATES.medicare.rate * 100).toFixed(2)}%)</span>
                        </div>
                        <span className="font-medium text-blue-900">
                          {formatCurrency(calculateMedicareTax(grossSalary, 0, w4Data?.filingStatus || 'single'))}
                        </span>
                      </div>
                      
                      {/* State Tax */}
                      {(user?.profile?.workState || compensation?.workState) && (
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span className="text-blue-700">State Tax</span>
                            <Badge variant="outline" className="text-xs">
                              {STATE_NAMES[user?.profile?.workState || compensation?.workState] || user?.profile?.workState || compensation?.workState}
                            </Badge>
                          </div>
                          <span className="font-medium text-blue-900">
                            {formatCurrency(calculateStateIncomeTax(
                              annualSalary,
                              user?.profile?.workState || compensation?.workState || 'CA',
                              w4Data?.filingStatus || 'single',
                              isUSPayroll ? (payFrequency === 'monthly' ? 12 : payFrequency === 'semimonthly' ? 24 : payFrequency === 'biweekly' ? 26 : 52) : 12
                            ))}
                          </span>
                        </div>
                      )}
                      
                      <Separator className="my-2" />
                      
                      {/* Total Estimated Taxes */}
                      <div className="flex justify-between items-center font-semibold">
                        <span className="text-blue-900">Total Est. Tax Withholding</span>
                        <span className="text-blue-900">
                          {(() => {
                            const periods = isUSPayroll ? (payFrequency === 'monthly' ? 12 : payFrequency === 'semimonthly' ? 24 : payFrequency === 'biweekly' ? 26 : 52) : 12;
                            return formatCurrency(
                              calculateFederalIncomeTax(annualSalary, w4Data?.filingStatus || 'single', w4Data?.additionalWithholding || 0, periods) +
                              calculateSocialSecurityTax(grossSalary) +
                              calculateMedicareTax(grossSalary, 0, w4Data?.filingStatus || 'single') +
                              calculateStateIncomeTax(annualSalary, user?.profile?.workState || compensation?.workState || 'CA', w4Data?.filingStatus || 'single', periods)
                            );
                          })()}
                        </span>
                      </div>
                      
                      <div className="mt-3 p-2 bg-amber-100 rounded text-xs text-amber-800 flex items-center gap-2">
                        <AlertCircle className="w-3 h-3" />
                        No payslips yet. Estimates based on your compensation setup.
                      </div>
                    </div>
                  </div>
                )}

                {/* W-4 Info Card */}
                <div className="p-4 bg-violet-50 rounded-lg">
                  <h4 className="font-medium text-violet-900 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    W-4 Information
                  </h4>
                  {w4Data ? (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-violet-600">Filing Status</p>
                        <p className="font-medium text-violet-900">
                          {FILING_STATUS_LABELS[w4Data.filingStatus] || w4Data.filingStatus}
                        </p>
                      </div>
                      <div>
                        <p className="text-violet-600">Multiple Jobs</p>
                        <p className="font-medium text-violet-900">
                          {w4Data.multipleJobs ? 'Yes' : 'No'}
                        </p>
                      </div>
                      <div>
                        <p className="text-violet-600">Dependent Credit</p>
                        <p className="font-medium text-violet-900">
                          {formatCurrency(w4Data.dependentCredit || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-violet-600">Additional Withholding</p>
                        <p className="font-medium text-violet-900">
                          {formatCurrency(w4Data.additionalWithholding || 0)}
                        </p>
                      </div>
                      {w4Data.effectiveDate && (
                        <div className="col-span-2">
                          <p className="text-violet-600">Effective Date</p>
                          <p className="font-medium text-violet-900">{formatDate(w4Data.effectiveDate)}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm">W-4 not on file. Contact HR to submit your W-4.</span>
                    </div>
                  )}
                </div>

                {/* Work Location */}
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <h4 className="font-medium text-emerald-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Tax Jurisdiction
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-emerald-600">Work State</p>
                      <p className="font-medium text-emerald-900">
                        {STATE_NAMES[user?.profile?.workState || compensation?.workState] || 'Not Set'}
                      </p>
                    </div>
                    <div>
                      <p className="text-emerald-600">Resident State</p>
                      <p className="font-medium text-emerald-900">
                        {STATE_NAMES[user?.profile?.residentState || compensation?.residentState] || 'Not Set'}
                      </p>
                    </div>
                  </div>
                  {(user?.profile?.workState !== user?.profile?.residentState) && user?.profile?.workState && user?.profile?.residentState && (
                    <div className="mt-3 p-2 bg-amber-100 rounded text-xs text-amber-800 flex items-center gap-2">
                      <AlertCircle className="w-3 h-3" />
                      You may be subject to taxes in both states
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Bonuses */}
        <TabsContent value="bonuses">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-600" />
                Bonuses & Incentives
              </CardTitle>
              <CardDescription>Your earned bonuses and incentive payments</CardDescription>
            </CardHeader>
            <CardContent>
              {bonuses.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Award className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p>No bonuses received yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {bonuses.map(bonus => (
                    <div key={bonus.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                          <Award className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{bonus.type} Bonus</p>
                          <p className="text-sm text-gray-500">{bonus.reason || 'Performance incentive'}</p>
                          <p className="text-xs text-gray-400 mt-1">{formatDate(bonus.approvedAt || bonus.createdAt)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-emerald-600">+{formatCurrency(bonus.amount)}</p>
                        <Badge className="bg-emerald-100 text-emerald-700 mt-1">Credited</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Revision History */}
        <TabsContent value="history">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-violet-600" />
                Salary Revision History
              </CardTitle>
              <CardDescription>Track your salary growth over time</CardDescription>
            </CardHeader>
            <CardContent>
              {salaryHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <History className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p>No revision history available</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Timeline */}
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  
                  <div className="space-y-6">
                    {salaryHistory.map((revision, idx) => (
                      <div key={idx} className="relative flex items-start gap-6 pl-12">
                        <div className="absolute left-4 w-4 h-4 bg-teal-600 rounded-full border-2 border-white"></div>
                        <div className="flex-1 p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-gray-900">
                              Salary Revised to {formatCurrency(revision.salary)}
                            </p>
                            {revision.incrementPercentage && (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <TrendingUp className="w-3 h-3 mr-1" />
                                +{revision.incrementPercentage}%
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            Effective from {formatDate(revision.effectiveFrom)}
                          </p>
                          {revision.reason && (
                            <p className="text-sm text-gray-600 mt-2">{revision.reason}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
